<template>
  <div
    v-if="modifiedSalesDocument && salesDocument"
    class="bottom-0 flex items-center justify-between h-auto px-3.5 overflow-auto bg-slate-100 max-h-22 gap-x-5 ring-1 ring-gray-900/10"
    :class="isCollapsed ? 'pt-1 pb-0' : 'p-2.5 pb-1'">
    <button @click="isCollapsed = !isCollapsed" class="text-theme-600">
      <IconMenu class="w-5"></IconMenu>
    </button>
    <div class="flex items-center justify-start gap-12">
      <div class="max-w-48">
        <label for="netTotal" class="text-sm font-medium capitalize" v-if="!isCollapsed">{{
          $t('salesDocuments.netTotal')
        }}</label>
        <BaseInput
          v-model="totalNet"
          class="h-8 text-sm font-light bg-white rounded-md"
          type="number"
          :min="0"
          :readonly="
            modifiedSalesDocument?.documentHeader.totalNetValue == 0 ||
            !modifiedSalesDocument.documentHeader.totalNetValue
          "
          :disabled="
            mode === 'view' ||
            modifiedSalesDocument.documentHeader?.documentType?.allowHeaderDiscounts === false
          "
          color="base"
          price
          :mode="isCollapsed ? 'view' : 'edit'"
          :error="$v.totalNet.$invalid ? $v.totalNet.maxValue.$message : ''"
          show-error-as-tooltip
          @blur="onTotalNetBlur"
          @change="onNetTotalChange" />
      </div>
      <div class="max-w-28">
        <label for="docNumber" class="text-sm font-medium" v-if="!isCollapsed">{{
          $t('salesDocuments.vat')
        }}</label>
        <BaseInput
          v-model="vatPercentage"
          readonly
          color="base"
          price
          class="h-8 text-sm font-light"
          :mode="isCollapsed ? 'view' : 'edit'" />
      </div>
      <div class="max-w-48">
        <label for="total" class="text-sm font-medium" v-if="!isCollapsed">{{
          $t('salesDocuments.total')
        }}</label>
        <BaseInput
          v-model="totalGross"
          class="h-8 text-sm font-light bg-white rounded-md"
          :readonly="
            modifiedSalesDocument?.documentHeader.totalGross == 0 ||
            !modifiedSalesDocument.documentHeader.totalGross
          "
          :disabled="
            mode === 'view' ||
            modifiedSalesDocument.documentHeader?.documentType?.allowHeaderDiscounts === false
          "
          type="number"
          color="base"
          icon="right"
          price
          :error="$v.totalGross.$invalid ? $v.totalGross.maxValue.$message : ''"
          show-error-as-tooltip
          @blur="onTotalGrossBlur"
          @change="onGrossTotalChange">
          <template #icon-right>
            <div
              class="flex items-center uppercase font-light px-1 h-[30px] text-gray-400 bg-slate-200 rounded-r-md border-l border-gray-300 text-xs">
              {{ '€' }}
            </div>
          </template>
        </BaseInput>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import IconMenu from '@/assets/icons/IconMenu.vue';
import BaseInput from '@/components/common/BaseInput.vue';
import { useSalesDocumentsStore } from '@/stores/salesDocuments';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';
import useVuelidate from '@vuelidate/core';
import { useI18n } from 'vue-i18n';
import { helpers } from '@vuelidate/validators';
import { eDiscountOrigin, type DocumentDetail } from '@/types/Documents';
import { computeAdditionalDiscountByPercentile, roundValue } from '@/utils/prices';
import { RecordMode } from '@/types/Base';

const { t: $t } = useI18n({ useScope: 'global' });
const { modifiedSalesDocument, salesDocument } = storeToRefs(useSalesDocumentsStore());
const { updateLineDiscounts, discountValueChange, updateHeaderTotals } = useSalesDocumentsStore();

const vatPercentage = computed(() => {
  if (!modifiedSalesDocument || !modifiedSalesDocument.value?.documentHeader.totalGross) return '-';
  return roundValue(modifiedSalesDocument.value?.documentHeader.totalVat || 0).toString();
});

const maxTotalGross = computed(() => {
  return roundValue(
    modifiedSalesDocument.value?.documentHeader.documentDetails
      ?.filter((d) => !d.deleted)
      .reduce(
        (s, d) =>
          roundValue(s + (d.totalBeforeDiscount ?? 0) * (1 + (d.vatFactorValue ?? 0) / 100)),
        0
      )
  );
});

const maxTotalNet = computed(() => {
  return roundValue(
    modifiedSalesDocument.value?.documentHeader.documentDetails
      ?.filter((d) => !d.deleted)
      .reduce((s, d) => roundValue(s + (d.totalBeforeDiscount ?? 0)), 0)
  );
});

const isCollapsed = ref(false);
const totalGross = ref();
const totalNet = ref();

defineProps<{
  mode: 'view' | 'edit';
}>();

onMounted(() => {
  if (modifiedSalesDocument.value) {
    totalGross.value = modifiedSalesDocument.value.documentHeader.totalGross;
    totalNet.value = modifiedSalesDocument.value.documentHeader.totalNetValue;
  }
});

watch(
  () => modifiedSalesDocument.value,
  (newData) => {
    totalGross.value = newData?.documentHeader.totalGross || 0;
    totalNet.value = newData?.documentHeader.totalNetValue || 0;
    validationModel.value.totalGross = totalGross.value;
    validationModel.value.totalNet = totalNet.value;
  },
  { deep: true }
);

const validationModel = ref({
  totalGross: totalGross.value || 0,
  totalNet: totalNet.value || 0,
});

const rules = computed(() => ({
  totalGross: {
    maxValue: helpers.withMessage(`${$t('messages.errors.invalidValue')}`, (value: any) => {
      const numberValue = Number(value);
      if (value < 0) return false;
      return maxTotalGross.value
        ? Number.isFinite(numberValue) && numberValue <= maxTotalGross.value
        : true;
    }),
  },
  totalNet: {
    maxValue: helpers.withMessage(`${$t('messages.errors.invalidValue')}`, (value: any) => {
      const numberValue = Number(value);
      if (value < 0) return false;
      return maxTotalNet.value
        ? Number.isFinite(numberValue) && numberValue <= maxTotalNet.value
        : true;
    }),
  },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

function onTotalGrossBlur() {
  if ($v.value.totalGross.$invalid || !totalGross.value) {
    totalGross.value = modifiedSalesDocument.value?.documentHeader.totalGross || 0;
    $v.value.totalGross.$reset();
  }
}

function onTotalNetBlur() {
  if ($v.value.totalNet.$invalid || !totalNet.value) {
    totalNet.value = modifiedSalesDocument.value?.documentHeader.totalNetValue || 0;
    $v.value.totalNet.$reset();
  }
}

async function onGrossTotalChange() {
  if (totalGross.value) $v.value.totalGross.$touch();
  if (
    !totalGross.value ||
    $v.value.totalGross.$invalid ||
    !modifiedSalesDocument.value ||
    !modifiedSalesDocument.value.documentHeader.documentDetails
  )
    return;

  if (modifiedSalesDocument.value && totalGross.value > maxTotalGross.value)
    totalGross.value = maxTotalGross.value;

  modifiedSalesDocument.value?.documentHeader.documentDetails
    .filter((l) => l.recordMode !== RecordMode.Deleted)
    .forEach((line: DocumentDetail) => {
      const additionalDiscountValue = computeAdditionalDiscountByPercentile(
        line.grossTotal,
        modifiedSalesDocument.value?.documentHeader.totalGross,
        totalGross.value,
        true,
        line.vatFactorValue
      );
      if (!additionalDiscountValue) return;

      updateDetailWithDiscount(line, additionalDiscountValue);
    });

  updateHeaderTotals();
}

async function onNetTotalChange() {
  if (totalNet.value) $v.value.totalNet.$touch();
  if (
    !totalNet.value ||
    $v.value.totalNet.$invalid ||
    !modifiedSalesDocument.value ||
    !modifiedSalesDocument.value?.documentHeader.documentDetails
  )
    return;
  if (modifiedSalesDocument.value && totalNet.value > maxTotalNet.value)
    totalNet.value = maxTotalNet.value;

  modifiedSalesDocument.value?.documentHeader.documentDetails
    .filter((l) => l.recordMode !== RecordMode.Deleted)
    .forEach((line: DocumentDetail) => {
      const additionalDiscountValue = computeAdditionalDiscountByPercentile(
        line.netTotal,
        modifiedSalesDocument.value?.documentHeader.totalNetValue,
        totalNet.value
      );
      if (!additionalDiscountValue) return;

      updateDetailWithDiscount(line, additionalDiscountValue);
    });

  updateHeaderTotals();
}

function updateDetailWithDiscount(line: DocumentDetail, additionalDiscountValue: number) {
  if (!modifiedSalesDocument.value?.documentHeader.documentDetails) return line;

  line.totalDiscount = roundValue(line.totalDiscount + additionalDiscountValue);
  line.unitDiscount = roundValue(line.totalDiscount / (line.totalQuantity || line.quantity));
  line = discountValueChange(line, '');

  line.documentDetailDiscounts = updateLineDiscounts(
    line,
    eDiscountOrigin.Header,
    additionalDiscountValue
  );
  if (line.recordMode != RecordMode.Added) line.recordMode = RecordMode.Modified;
  return line;
}
</script>
